import { Component, OnDestroy, OnInit, input, inject } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ApContentService, ApKeyOverridePipe, ApContentPipe, ApContentComponent } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { ApCheckboxComponent } from '@ap/checkbox';
import { ApInputComponent } from '@ap/input';
import { ApDiacriticsNormalizeFormValueDirective } from '@ap/diacritics-normalize';
import { ApRadioGroupComponent, ApRadioButtonComponent } from '@ap/radio';
import { ApNotificationComponent } from '@ap/notification';

import { FieldLengths } from '../personal-details.expressions';

import content from './partner-details.content.json';
import { PartnerDetailsForm, Salutation } from './partner-details.model';

/** This component can be used to enter details about partner name */
@Component({
    selector: 'ap-partner-details',
    templateUrl: './partner-details.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ApCheckboxComponent,
        ApInputComponent,
        ApDiacriticsNormalizeFormValueDirective,
        ApRadioGroupComponent,
        ApRadioButtonComponent,
        ApNotificationComponent,
        ApContentComponent,
        ApContentPipe,
        ApKeyOverridePipe
    ]
})
export class ApPartnerDetailsComponent implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);

    /**
     * Required - The form group to bind to (see @link ApPartnerDetailsService)
     */
    public readonly partnerDetailsForm = input.required<FormGroup<PartnerDetailsForm>>();

    /**
     * Keymap for content overrides
     */
    public readonly contentKeyMap = input<{
        [contentKey: string]: string;
    }>();

    protected lastNameDiacritic = false;
    protected partnerInsertionErrorMessages: ErrorMessages;
    protected partnerLastNameErrorMessages: ErrorMessages;
    protected salutationErrorMessages: ErrorMessages;

    protected showPartnerFields = false;
    protected salutations: { key: string; value: number }[] = [];

    public formSubscription = null;

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.showPartnerFields = this.partnerDetailsForm().get('usePartnerName').value;
        this.salutations = Salutation.keys().map((key) => ({
            key,
            value: Salutation[key] as number
        }));

        this.showOrHideDetails();
        this.setErrorMessages();

        this.formSubscription = this.partnerDetailsForm().valueChanges.subscribe(this.onFormValueChanges.bind(this));
    }

    public ngOnDestroy(): void {
        this.onFormValueChanges(this);
    }

    private onFormValueChanges(changes): any {
        if (changes.hasOwnProperty('usePartnerName') && changes.usePartnerName !== this.showPartnerFields) {
            this.toggle();
        }
    }

    protected toggle(): void {
        this.showPartnerFields = !this.showPartnerFields;
        this.showOrHideDetails();

        if (this.showPartnerFields) {
            this.partnerDetailsForm().get('salutation').setValue(null);
        } else if (!this.showPartnerFields) {
            this.partnerDetailsForm().get('salutation').setValue(Salutation.birthName);
        }
    }

    protected getErrorMessage(field: string, error: string): string {
        return this.contentService.get(new ApKeyOverridePipe().transform(`partner.details.${field}.error.${error}`, this.contentKeyMap()));
    }

    protected isLastNameRequired(): boolean {
        return this.partnerDetailsForm().get('salutation').value !== Salutation.birthName;
    }

    protected diacriticsChange(diacriticValue: boolean): void {
        this.lastNameDiacritic = diacriticValue;
    }

    protected getLastName(): string {
        return this.partnerDetailsForm().get('partnerLastName').value;
    }

    private showOrHideDetails(): void {
        if (this.showPartnerFields) {
            this.showPartnerDetails();
        } else {
            this.hidePartnerDetails();
        }
    }

    private setErrorMessages(): void {
        this.salutationErrorMessages = {
            required: this.contentService.get('partner.details.salutation.error.required')
        };
        this.partnerInsertionErrorMessages = {
            incorrect: this.contentService.get('partner.details.partnerInsertion.error.incorrect'),
            maxlength: this.contentService.get('partner.details.partnerInsertion.error.maxlength', {
                maxLength: FieldLengths.INSERTION.toString()
            }),
            insertionAllowed: this.contentService.get('partner.details.partnerInsertion.error.insertionAllowed')
        };
        this.partnerLastNameErrorMessages = {
            required: this.contentService.get('partner.details.partnerLastName.error.required'),
            incorrect: this.contentService.get('partner.details.partnerLastName.error.incorrect')
        };
    }

    private showPartnerDetails(): void {
        this.enableControls();
    }

    private hidePartnerDetails(): void {
        this.disableControls();
    }

    private enableControls(): void {
        this.getFormControls()
            .filter(({ controlName }) => controlName === 'partnerInsertion' || controlName === 'partnerLastName')
            .forEach(({ control }) => control.enable());
    }

    private disableControls(): void {
        this.getFormControls()
            .filter(({ controlName }) => controlName === 'partnerInsertion' || controlName === 'partnerLastName')
            .forEach(({ control }) => control.disable());
    }

    private getFormControls(): {
        controlName: string;
        control: AbstractControl;
    }[] {
        return Object.keys(this.partnerDetailsForm().controls).map((key) => ({
            controlName: key,
            control: this.partnerDetailsForm().controls[key]
        }));
    }
}
